import { useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import {
  UniversalFilterOperationStateStatus,
  UniversalFilterOperationStateValue,
  UniversalFilterOperationStateModes,
  UniversalFilterOperationStateFilterState,
  UniversalFilterOperationStateOnChange
} from '../../UniversalFilterOperationState.types';

interface UniversalFilterOperationStateFilterStateOptions {
  onChange: UniversalFilterOperationStateOnChange;
}

function useUniversalFilterOperationStateFilterState({
  onChange
}: UniversalFilterOperationStateFilterStateOptions) {
  // All modes and options available in handleApplyFilterState
  // must be implemented in handleParseFilterValue too
  const handleApplyFilterState = useCallback<
    (filterState: UniversalFilterOperationStateFilterState) => void
  >(
    (filterState) => {
      const states = filterState?.states;

      if (filterState?.mode === UniversalFilterOperationStateModes.IS_ANY_OF) {
        onChange(isEmpty(states) ? undefined : { in: states });
        return;
      }

      if (filterState?.mode === UniversalFilterOperationStateModes.IS_NONE_OF) {
        onChange(isEmpty(states) ? undefined : { notIn: states });
        return;
      }

      onChange();
    },
    [onChange]
  );

  const handleParseFilterValue = useCallback<
    (
      filterValue: UniversalFilterOperationStateValue
    ) => UniversalFilterOperationStateFilterState | undefined
  >((filterValue) => {
    if (isNil(filterValue)) {
      return undefined;
    }

    if (!isEmpty(filterValue.in)) {
      return {
        mode: UniversalFilterOperationStateModes.IS_ANY_OF,
        states: filterValue.in as UniversalFilterOperationStateStatus[]
      };
    }

    if (!isEmpty(filterValue.notIn)) {
      return {
        mode: UniversalFilterOperationStateModes.IS_NONE_OF,
        states: filterValue.notIn as UniversalFilterOperationStateStatus[]
      };
    }

    return undefined;
  }, []);

  return { handleApplyFilterState, handleParseFilterValue };
}

export default useUniversalFilterOperationStateFilterState;
